import React, { useEffect, useState } from "react";
import { useStoreState } from "ezStore";
import { useMutation, useQuery } from "@apollo/client";

import { Button, Card, FormGroup } from "@cofano/cck";

import { Tabs, Tab, Box, Tooltip, CircularProgress, Stack } from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/pro-duotone-svg-icons";

import LastShippedProducts from "components/products/LastShippedProducts";
import useTabs from "components/hooks/useTabs";

import StatusChanger from "view/statuses/components/changer/StatusChanger";
import RelationSelect from "view/relations/components/selects/RelationSelect";
import { CargoActionDate } from "view/cargos/components/date/CargoActionDate";

import { DossierLink, LocationLink, ProductLink, RelationLink, ShipLink, VoyageLink } from "utils/LinkUtils";

import { DataFormat, hasPluginPermission, Language, usePermission } from "imports/Utils";

import { ChildProps } from "types/commons";

import { GetCargoForSideviewerDocument, GetCargoForSideviewerQuery } from "__generated__/view/cargos/graphql/cargo.queries.generated";
import { SaveCargoSupplierDocument } from "__generated__/view/cargos/graphql/cargo.mutations.generated";
import { CallType, StatusType, RelationType } from "__generated__/enums";

enum CargoSideviewerTabs {
	CargoActions = "cargoActions",
	Relations = "releations"
}

type CargoFromQuery = ChildProps<GetCargoForSideviewerQuery, "cargo">;

const CargoSideviewer = ({
	cargoId,
	onChange,
} : {
	cargoId: number,
	onChange: () => void;
}) => {

	const session = useStoreState(store => store.session);

	const { isAdmin, isCompanyUser, isTerminal, isFinance, isMainUserLimited, isCustomer, isReadOnly } = usePermission();
	const canUseContracts = isAdmin || isFinance || isMainUserLimited || isCompanyUser;
	const canEditCargo = !isReadOnly && (isAdmin || isMainUserLimited || isCompanyUser);

	const { selectedTab, selectTab } = useTabs<CargoSideviewerTabs>({
		defaultSelected: CargoSideviewerTabs.CargoActions,
		disableParams: true,
	});

	const { data, loading, refetch } = useQuery(GetCargoForSideviewerDocument, {
		variables: {
			id: cargoId,
		},
	});

	const cargo = data?.cargo;

	const [ selectedActionId, selectActionId ] = useState(cargo?.actions?.[0]?.id ?? null);
	const currentAction = cargo?.actions?.find(it => it.id === selectedActionId);

	const onRefresh = () => {
		refetch();
		onChange();
	};

	useEffect(() => {
		if (cargo?.actions?.[0]) {
			selectActionId(cargo.actions[0].id);
		}
	}, [ cargo?.id ]);

	if (loading || !cargo) {
		return (
			<Box p={2}>
				<CircularProgress size={24} />
			</Box>
		);
	}

	return (
		<Box p={2}>
			<Card
				title={(
					<span>
						<ProductLink product={cargo.product} />

						{cargo.product && !cargo.product?.densityKgM3 && (
							<Tooltip title={Language("PRODUCT_NO_DENSITY_WARNING")} placement="top">
								<span style={{ marginLeft: "16px" }}>
									<FontAwesomeIcon
										icon={faExclamationTriangle}
										color="red" />
								</span>
							</Tooltip>
						)}
					</span>
				)}
				actions={[ {
					render: () => (
						<StatusChanger
							entity={cargo}
							statusType={StatusType.Cargo}
							refetch={onRefresh}
							showHistory />
					),
				} ]}
				disablePadding>
				<React.Fragment>
					<Box sx={{ borderBottom: 1, borderColor: "divider" }}>
						<Tabs value={selectedTab} onChange={(_event, newValue) => selectTab(newValue)}>
							<Tab label={Language("CARGO_ACTIONS")} value={CargoSideviewerTabs.CargoActions} />
							<Tab label={Language("RELATIONS")} value={CargoSideviewerTabs.Relations} />
						</Tabs>
					</Box>

					{selectedTab === CargoSideviewerTabs.CargoActions && (
						<Box sx={{ borderBottom: 1, borderColor: "divider" }}>
							<Tabs
								value={selectedActionId}
								onChange={(_event, newValue) => selectActionId(newValue)}
								variant="scrollable"
								scrollButtons="auto">
								{cargo.actions.map(action => (
									<Tab
										value={action.id}
										label={(
											<span style={{ alignItems: "center", display: "flex", maxWidth: "200px" }}>
												{action.location?.description}
												<Tooltip title={action.type === CallType.Loading ? Language("CALL_TYPE_0") : Language("CALL_TYPE_1")} placement="top">
													<span>
														{action.type === CallType.Loading ?
															<ArrowDownwardIcon fontSize="small" /> :
															<ArrowUpwardIcon  fontSize="small" />}
													</span>
												</Tooltip>
											</span>
										)} />
								))}
							</Tabs>
						</Box>
					)}

					<Box p={2}>
						{selectedTab === CargoSideviewerTabs.CargoActions && currentAction && (
							<>
								<FormGroup label={Language("LOCATION")} labelPlacement="left-center" overflow="hidden">
									<LocationLink location={currentAction.location} />
								</FormGroup>

								<FormGroup label={Language("WEIGHT")} labelPlacement="left-center" overflow="hidden">
									{DataFormat.tonnage(currentAction.weight)}
								</FormGroup>

								<FormGroup label={Language("CARGO_VOLUME")} labelPlacement="left-center" overflow="hidden">
									{DataFormat.volumeM3(currentAction.volume)}
								</FormGroup>

								<FormGroup label={Language("DATE")} labelPlacement="left-center" overflow="hidden">
									<CargoActionDate action={currentAction} withoutYear />
								</FormGroup>
							</>
						)}

						{selectedTab === CargoSideviewerTabs.Relations && (
							<FormGroup label={Language("SUPPLIER")} labelPlacement="left-center" overflow="hidden">
								<SupplierField
									cargo={cargo}
									disabled={!canEditCargo}
									onRefresh={onRefresh}
								/>
							</FormGroup>
						)}
					</Box>
				</React.Fragment>
			</Card>

			<Box mt={2} mb={2}>
				<Card>
					<FormGroup
						label={Language("VOYAGE")}
						labelPlacement="left"
						labelPaddingTop={0}
						overflow="hidden">
						<Stack spacing={1}>
							<Box>
								<VoyageLink voyage={cargo.voyage} />
							</Box>

							<Box>
								{cargo.voyage && (
									<StatusChanger
										entity={cargo.voyage}
										statusType={StatusType.Voyage}
										refetch={onRefresh}
										showHistory />
								)}
							</Box>
						</Stack>
					</FormGroup>

					<FormGroup label={Language("SHIP")} labelPlacement="left-center" overflow="hidden">
						<ShipLink ship={cargo.voyage?.ship} />
					</FormGroup>

					{hasPluginPermission(session, "showLastShippedProducts") && (!isCustomer || !isTerminal) && (
						<React.Fragment>
							{loading ? (
								<CircularProgress size={18} />
							) : (
								<LastShippedProducts
									voyage={cargo.voyage ?? undefined}
									ship={cargo.voyage?.ship}
									refetch={onRefresh} />
							)}
						</React.Fragment>
					)}
				</Card>
			</Box>

			{(canUseContracts && cargo.charter) && (
				<Card>
					<FormGroup label={Language("CHARTER_ORDER_NUMBER")} labelPlacement="left-center" overflow="hidden">
						<DossierLink charter={cargo.charter} />
					</FormGroup>
				</Card>
			)}
		</Box>
	);
};

const SupplierField = ({
	cargo,
	disabled,
	onRefresh,
}: {
	cargo: CargoFromQuery,
	disabled: boolean;
	onRefresh: () => void;
}) => {

	const [ editMode, setEditMode ] = useState(false);
	const [ selectedSupplier, setSelectedSupplier ] = useState(cargo.supplier ?? null);

	const [ saveCargoSupplier, { loading } ] = useMutation(SaveCargoSupplierDocument);

	return (
		<Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, alignItems: "center" }}>
			{editMode ? (
				<>
					<RelationSelect
						value={selectedSupplier}
						onChange={supplier => setSelectedSupplier(supplier)}
						types={[ RelationType.Supplier ]}
					/>

					<Box sx={{ display: "flex", gap: 1 }}>
						<Button
							size="small"
							variant="text"
							color="danger"
							onClick={() => setEditMode(false)}
							icon={<CloseRoundedIcon fontSize="small" />}
						/>

						<Button
							size="small"
							variant="text"
							loading={loading}
							icon={<SaveRoundedIcon fontSize="small" />}
							disabled={!selectedSupplier}
							onClick={() => {
								if (selectedSupplier) {
									saveCargoSupplier({
										variables: {
											cargoId: cargo.id,
											supplierId: selectedSupplier.id,
										},
									}).then(() => {
										setEditMode(false);
										onRefresh();
									});
								}
							}}
						/>
					</Box>
				</>
			) : (
				<>
					<RelationLink relation={cargo.supplier} />

					<Button
						size="small"
						variant="text"
						disabled={disabled}
						icon={<EditRoundedIcon fontSize="small" />}
						onClick={() => setEditMode(true)}
					/>
				</>
			)}
		</Box>
	);
};

export default CargoSideviewer;
